import { Inbox, RefreshCw, RotateCcw } from "lucide-react";
import * as React from "react";
import { Toolbar } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { CallPager, CallRangeLabel } from "@/features/call-report";
import { ApiError } from "@/lib/api";
import {
  REQUESTS_PAGE_SIZE, useReplayTriggerRequest, useTriggerInbox, useTriggerRequest,
} from "@/lib/queries";
import type { Trigger } from "@/lib/types";
import { INBOX_STATUSES, inboxStatusText, requestPaths, resultText } from "@/lib/triggers";
import { formatDateTime } from "@/lib/utils";

const ALL = "all";

/**
 * What a trigger received, newest first, and what each request made (ADR-021). A page carries no
 * request bodies: they hold names and numbers, so one is read only when its request is opened.
 */
export function TriggerRequestsCard({ trigger }: { trigger: Trigger }) {
  const [status, setStatus] = React.useState(ALL);
  const [page, setPage] = React.useState(0);
  const [opened, setOpened] = React.useState<number>();
  const filters = { status: status === ALL ? undefined : status };
  const { data, isPending, isFetching, error, refetch } = useTriggerInbox(trigger.id, filters, page);

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Requests"
        description="Everything this trigger received, and what each made. Open one to read what the sender sent."
        action={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh the requests"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <RefreshCw />
          </Button>
        }
      />
      <Toolbar>
        <Select
          value={status}
          onValueChange={(next) => {
            setStatus(next);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-44" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Every status</SelectItem>
            {INBOX_STATUSES.map((s) => (
              <SelectItem key={s} value={s} description={inboxStatusText(s).description}>
                {inboxStatusText(s).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-text-muted">
          {data && <CallRangeLabel from={page * REQUESTS_PAGE_SIZE} count={data.items.length} total={data.total} />}
        </span>
      </Toolbar>

      {isPending ? (
        <TableSkeleton cols={4} />
      ) : error ? (
        <ErrorState
          error={error}
          title="Could not load the requests"
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={status === ALL ? "Nothing received yet" : "No requests match"}
          description={
            status === ALL
              ? "Send this trigger a request, from a zap's test or the command above, and it shows up here."
              : "Try another status."
          }
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>What it made</TableHead>
                <TableHead className="text-right">Tries</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => {
                const state = inboxStatusText(row.status);
                const made = resultText(row, trigger.action, formatDateTime);
                return (
                  <TableRow
                    key={row.id}
                    interactive
                    tabIndex={0}
                    onClick={() => setOpened(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpened(row.id);
                      }
                    }}
                  >
                    <TableCell className="whitespace-nowrap text-xs text-text-muted">
                      {formatDateTime(row.received_at)}
                    </TableCell>
                    <TableCell>
                      <Badge tone={state.tone} size="sm" title={state.description}>
                        {row.status === "received" && <Spinner className="size-2.5" />}
                        {state.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md truncate text-xs text-text" title={made}>
                      {made}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{row.attempts}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <CallPager
            page={page}
            count={data.items.length}
            total={data.total}
            onChange={setPage}
            busy={isFetching}
            pageSize={REQUESTS_PAGE_SIZE}
          />
        </>
      )}

      <TriggerRequestDrawer trigger={trigger} rowId={opened} onClose={() => setOpened(undefined)} />
    </Card>
  );
}

/**
 * One request: what the sender sent, the fields a mapping can name in it, and what it made. Applying
 * it again runs the trigger as it now stands, which is how a request that came before its mapping
 * was right, or before its lead existed, is put through.
 */
export function TriggerRequestDrawer({
  trigger, rowId, onClose,
}: {
  trigger: Trigger;
  rowId: number | undefined;
  onClose: () => void;
}) {
  const toast = useToast();
  const { data, isPending, error, refetch } = useTriggerRequest(trigger.id, rowId);
  const replay = useReplayTriggerRequest();
  const state = data ? inboxStatusText(data.status) : null;
  const fields = data?.raw ? requestPaths(data.raw) : [];

  const applyAgain = async () => {
    if (!data) return;
    try {
      await replay.mutateAsync({ id: trigger.id, rowId: data.id });
      toast.success("Applying it again", trigger.name);
    } catch (e) {
      toast.error("Could not apply it again", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Dialog open={rowId !== undefined} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="drawer" size="lg">
        <DialogHeader>
          <DialogTitle>{data ? `Request received ${formatDateTime(data.received_at)}` : "Request"}</DialogTitle>
          <DialogDescription>{trigger.name}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isPending ? (
            <div className="flex min-h-40 items-center justify-center">
              <Spinner className="size-5" />
            </div>
          ) : error ? (
            <ErrorState
              error={error}
              title="Could not read the request"
              action={
                <Button variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
            />
          ) : data && state ? (
            <>
              <div className="space-y-1.5">
                <Badge tone={state.tone}>
                  {data.status === "received" && <Spinner className="size-2.5" />}
                  {state.label}
                </Badge>
                <p className="text-sm text-text">{resultText(data, trigger.action, formatDateTime)}</p>
              </div>
              <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2 text-xs">
                <dt className="text-text-muted">Tries</dt>
                <dd className="tabular-nums text-text">{data.attempts}</dd>
                <dt className="text-text-muted">Applied</dt>
                <dd className="text-text">{data.applied_at ? formatDateTime(data.applied_at) : "—"}</dd>
                <dt className="text-text-muted">Known by</dt>
                <dd className="break-all font-mono text-text-muted" title="A request with the same key is taken as this one">
                  {data.idempotency_key}
                </dd>
              </dl>

              {data.raw ? (
                <>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-text">What the sender sent</p>
                      <CopyButton value={JSON.stringify(data.raw, null, 2)} label="Copy the request" />
                    </div>
                    <pre className="max-h-72 overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
                      {JSON.stringify(data.raw, null, 2)}
                    </pre>
                  </div>
                  {fields.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-text">Paths a mapping can name in it</p>
                      <div className="max-h-48 overflow-auto rounded-md border border-border">
                        {fields.map((field) => (
                          <div
                            key={field.path}
                            className="flex items-center justify-between gap-3 border-b border-border px-3 py-1 last:border-0"
                          >
                            <span className="inline-flex items-center gap-1 font-mono text-xs text-text">
                              {field.path}
                              <CopyButton value={field.path} label={`Copy ${field.path}`} />
                            </span>
                            <span className="truncate text-xs text-text-muted">{field.example}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Callout tone="info">
                  What the sender sent was emptied after the retention period. What it made is kept.
                </Callout>
              )}

              {data.result && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-text">What the action wrote</p>
                  <pre className="max-h-48 overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text-muted">
                    {JSON.stringify(data.result, null, 2)}
                  </pre>
                </div>
              )}
            </>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="default"
            disabled={!data?.raw || data.status === "received"}
            loading={replay.isPending}
            onClick={() => void applyAgain()}
          >
            <RotateCcw />
            Apply again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
